import { Action, Link, YapAmount, YapMoney } from '@/types/general';
import { AttachedReceipt, TrxReceipt } from './receiptFile';

export enum TransactionEndPoints {
  AdvancedSearch = '/transactions/advanced-search',
  SpendingStats = '/spendings/timeseries'
}

export enum TrxBookingType {
  Credit = 'credit',
  Debit = 'debit'
}

export enum TransactionRejectionType {
  Decline = 'decline',
  Reversed_by_merchant = 'reversed_by_merchant',
  Manual_release = 'manual_release',
  Reversed_issuer_expiration = 'reversed_issuer_expiration'
}

export enum TrxStatus {
  Booked = 'booked',
  Cancelled = 'cancelled',
  Fraud = 'fraud',
  Rejected = 'rejected',
  Reserved = 'reserved'
}

export interface TrxFiltersType {
  fromDate?: string;
  toDate?: string;
  trxType?: Array<string>;
  amountFrom?: string;
  amountTo?: string;
  bookingType?: string;
  cards_uids?: Array<string>;
}

interface TrxFee {
  amount: string;
  currency: string;
  type_no: number;
  type: string;
}

export interface RejectionDetails {
  code?: string;
  rejectType?: TransactionRejectionType;
  memo?: string;
  additionalInformation?: string;
}

export interface EmbeddedGeolocationData {
  lng?: string;
  lat?: string;
}

export interface EmbeddedLocationData {
  address: string;
  geolocation?: EmbeddedGeolocationData;
}

interface EmbeddedLogoData {
  id: string;
}

interface EmbeddedBannerData {
  id: string;
}

interface EmbeddedFacilitatorData {
  uid: string;
  name: string;
  logo?: EmbeddedLogoData;
  banner?: EmbeddedBannerData;
}

interface EmbeddedCardData {
  uid: string;
  lastFour: string;
  holderName: string;
}

interface EmbeddedAccountData {
  uid: string;
}

interface EmbeddedInstituteData {
  id: string;
  name: string;
}

interface EmbeddedCounterpartData {
  iban: string;
  name: string;
  addressLines: string[];
  note: string;
  institute?: EmbeddedInstituteData;
  country?: string;
}

interface EmbeddedPaymentOrderData {
  uid: string;
}

export interface Transaction {
  actionDate: string; // v2 OK
  /**
   * @deprecated use `amountMoney` instead
   */
  amount?: string;
  amountMoney?: YapMoney; // v2 OK
  totalAmountMoney?: YapMoney; // v2 OK
  totalFeeMoney?: YapMoney; // v2 OK
  originalMoney: YapMoney; // v2 OK
  originalRate?: YapAmount; // v2 OK
  bookingDate?: string; // v2 OK
  bookingType?: TrxBookingType; // v2 OK
  category?: string; // v2 OK
  // userNote?: string; /** @deprecated does not exit anymore */
  transactionTags?: Array<string>;
  receipt?: TrxReceipt;
  counterpartName?: string; // v2 OK
  counterpartMyPicture_uri?: string; // v2 OK
  /**
   * @deprecated use `currency` from `xyzMoney` instead
   */
  currency?: string;
  description?: string; // v2 OK
  attachedReceipt?: AttachedReceipt; // v2 OK
  rejectionDetails?: RejectionDetails; // v2 ??
  /**
   * @deprecated debug is not present in v2
   */
  debug?: {
    description?: string;
    esr?: string;
    iban?: string;
    viban?: string;
    ultimateCreditorName?: string;
    ultimateCreditorAddress?: string;
    ultimateCreditorAddressLines?: string;
    ultimateDebitorName?: string;
    ultimateDebitorAddress?: string;
    ultimateDebitorAddressLines?: string;
    country?: string;
    currency?: string;
    extTransactionRef?: string;
  };
  digitalWallet?: string;
  ultimateCreditorName?: string;
  ultimateDebitorName?: string;
  mcc?: string;
  /** @deprecated use `originalMoney` & `originalRate` instead */
  original?: {
    amount?: string;
    currency?: string;
    fxRate?: string;
  };
  reimbursementState?: string;
  status: string; // v2 OK
  // totalAmount?: string; /** @deprecated use `totalAmountMoney` instead */
  typeGroup?: string; // v2 OK
  // /** @deprecated use `totalFeeMoney` instead */
  // totalFees?: {
  //   amount: string;
  //   currency: string;
  // };
  // fees?: Array<TrxFee>; /** @deprecated this does not exist anymore? */
  type_cd?: string; // v2 OK
  type_no?: string; // v2 OK
  type_temp?: string; // v2 OK
  paymentType?: string; // v2 OK
  invoice?: { uid: string };
  merchant?: {
    id: string;
    name: string;
    category: string;
    location?: EmbeddedLocationData;
    logo?: EmbeddedLogoData;
    banner?: EmbeddedBannerData;
  };
  company?: { uid: string; name: string };
  facilitator?: EmbeddedFacilitatorData;
  card?: EmbeddedCardData;
  account?: EmbeddedAccountData;
  counterpart?: EmbeddedCounterpartData;
  paymentOrder?: EmbeddedPaymentOrderData;
  _links: {
    self: Link;
    cashAccount?: Link;
    /** @deprecated will not be available in v2 */
    counterpart?: {
      uri: string;
      embedded: {
        iban?: string;
        name: string;
        addressLines?: Array<string>;
        note: string;
        instituteId: string;
        instituteName: string;
      };
    };
    /** @deprecated will not be available in v2 */
    company?: {
      embedded?: {
        banner_uri?: string;
        logo_uri?: string;
        name?: string;
      };
      uri?: string;
    };
    /** @deprecated will not be available in v2 */
    facilitator?: {
      embedded?: {
        banner_uri?: string;
        logo_uri?: string;
        name?: string;
      };
      uri?: string;
    };
    /** @deprecated will not be available in v2 */
    invoice?: {
      uri?: string;
    };
    /** @deprecated will not be available in v2 */
    pdf?: {
      uri: string;
    };
    /** @deprecated will not be available in v2 */
    merchant?: {
      embedded?: {
        banner_uri?: string;
        category?: string;
        location?: {
          address?: string;
          geolocation?: EmbeddedGeolocationData;
        };
        logo_uri?: string;
        name?: string;
      };
      uri?: string;
    };
    /** @deprecated will not be available in v2 */
    relatedTransaction?: {
      uri?: string;
      type?: string;
    };
    /** @deprecated will not be available in v2 */
    paymentOrder?: Link;
    /** @deprecated will not be available in v2 */
    card?: {
      uri: string;
      embedded?: {
        holderName: string;
        lastDigits: string;
      };
    };
  };
  /** @deprecated _actions will not be available in v2 */
  _actions?: {
    customize: Action;
    requestReimbursement: Action;
  };
}
